import { useEffect } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';

import warningImage from '../../../assets/warn-icon.svg';
import Button from '../../../components/buttons/Button';
import { DialogBox } from '../../../components/Dialog/DialogBox';
import { DialogContent } from '../../../components/Dialog/DialogContent';
import { DialogHeader } from '../../../components/Dialog/DialogHeader';
import { DialogLoader } from '../../../components/Dialog/DialogLoader';
import type { WizardChildProps } from '../../../components/Wizard/types';
import { analytics } from '../../../services/analytics';
import { auth } from '../../../services/auth';
import { intercom } from '../../../services/intercom';
import { useStores } from '../../../stores/useStores';
import { useOpenAuthWizard } from '../useOpenAuthWizard';

import styles from './SomethingWentWrongDialog.module.scss';

const SomethingWentWrongDialog = observer((props?: WizardChildProps) => {
  const { authStore } = useStores();
  const { openAuthWizard } = useOpenAuthWizard();
  const { t } = useTranslation();

  const { isSigningUp } = authStore;
  const landingKey = authStore.getLandingKey() ?? undefined;
  const isDirectLanding = authStore.isDirectLanding();

  const isPhoneVerificationError = !auth.isAuthError;

  const handleContactSupport = () => {
    if (isPhoneVerificationError) {
      void analytics.sendEvent('phone_verification_error', {
        event_action: 'click',
        event_category: 'phone_verification',
        click_location: 'contact_support',
      });
    }

    intercom.handleOpenSupport();
  };

  const handleClose = () => {
    if (auth.isAuthError) {
      openAuthWizard('signUpWizard', {
        triggeredBy: 'auth_error',
        force: true,
        persistent: isDirectLanding,
        landingKey,
      });
      auth.isAuthError = false;
    } else {
      void analytics.sendEvent('phone_verification_error', {
        event_action: 'click',
        event_category: 'phone_verification',
        click_location: 'close',
      });

      openAuthWizard('phoneVerificationWizard', {
        force: true,
      });
    }
  };

  const handleTryAgain = () => {
    if (auth.isAuthError) {
      openAuthWizard('signUpWizard', {
        triggeredBy: 'auth_error',
        force: true,
        persistent: isDirectLanding,
        landingKey,
      });

      auth.isAuthError = false;
    } else {
      void analytics.sendEvent('phone_verification_fill_details', {
        event_action: 'click',
        event_category: 'phone_verification',
        click_location: 'try_again',
      });

      openAuthWizard('phoneVerificationWizard', {
        force: true,
      });
    }
  };

  useEffect(() => {
    if (isPhoneVerificationError && props?.isCurrent) {
      void analytics.sendEvent('phone_verification_error', {
        event_action: 'impression',
        event_category: 'phone_verification',
      });
    }
  }, [isPhoneVerificationError, props?.isCurrent]);

  return (
    <DialogBox>
      <DialogHeader className={styles.title} title={`${t('dialogs.oops')}`} onClose={handleClose} />
      <DialogLoader isProcessing={isSigningUp} />

      <DialogContent>
        <div className={styles.somethingWentWrongDialog}>
          <div data-testid="something-went-wrong-title">
            <Trans
              components={{
                title: <div className={classNames(styles.textBold, styles.textHeight)} />,
                accent: <span className={classNames(styles.textBold, styles.textOrange)} />,
              }}
              i18nKey="errors.itSeemsSomethingWentWrong"
            />
          </div>
          <img alt="Warning" className={styles.warningImage} src={warningImage} />
          <div className={styles.subTitle}>
            <span>{t('errors.dontWorryWeHereToHelp')}</span>
          </div>
          <Button btnType="main" type="submit" onClick={handleTryAgain}>
            {t('common.tryAgainUpper')}
          </Button>
          <Button btnType="main" type="submit" onClick={handleContactSupport}>
            {t('common.contactSupportUpperCase')}
          </Button>
        </div>
      </DialogContent>
    </DialogBox>
  );
});

export default SomethingWentWrongDialog;
